import * as React from "react";
import { Box, InputLabel, SelectChangeEvent } from "@mui/material";
import { useSearchParams } from "react-router-dom";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs, { Dayjs } from "dayjs";
import { FilterRowItem } from "./styles";
import { getValueAsEvent } from "./filters";

interface Props {
  updateSearch: (key: string, event: SelectChangeEvent<string>) => void;
}

// "Pros Added Since" date-range filter.
//
// Implemented with two FREE @mui/x-date-pickers `DatePicker`s (From / To)
// instead of the Pro `DateRangePicker`. The MUI X Pro license THROWS in
// development once expired, which crashed the whole Filters modal. The free
// pickers carry no license check and produce the identical `daterange` param
// (`startTimestamp,endTimestamp`) the backend already consumes.
export function RangeFilter({ updateSearch }: Props) {
  const [searchParams] = useSearchParams();

  const [start, end] = React.useMemo<[Dayjs | null, Dayjs | null]>(() => {
    const daterange = searchParams.get("daterange")?.split(",");
    const s = daterange?.[0] ? dayjs(parseInt(daterange[0], 10)) : null;
    const e = daterange?.[1] ? dayjs(parseInt(daterange[1], 10)) : null;
    return [s, e];
  }, [searchParams]);

  // Emit the same `start,end` timestamp pair the Pro range picker did. End
  // defaults to end-of-today when only a start is chosen; an empty start clears
  // the filter entirely.
  const pushRange = (s: Dayjs | null, e: Dayjs | null) => {
    let daterange = "";
    if (s) {
      const startTimestamp = s.valueOf();
      const endTimestamp = e
        ? e.endOf("day").valueOf()
        : dayjs().endOf("day").valueOf();
      daterange = `${startTimestamp},${endTimestamp}`;
    }
    updateSearch("daterange", getValueAsEvent(daterange));
  };

  return (
    <FilterRowItem>
      <InputLabel>Pros Added Since:</InputLabel>
      <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="en-us">
        <Box display="flex" alignItems="center" sx={{ width: "100%", gap: 2 }}>
          <DatePicker
            label="From"
            value={start}
            disableFuture
            onChange={(newStart: Dayjs | null) => pushRange(newStart, end)}
            slotProps={{
              field: {
                clearable: true,
                onClear: () => pushRange(null, end),
              } as any,
              textField: { fullWidth: true, size: "small" },
            }}
            sx={{ width: "100%" }}
          />
          <DatePicker
            label="To"
            value={end}
            disableFuture
            minDate={start || undefined}
            onChange={(newEnd: Dayjs | null) => pushRange(start, newEnd)}
            slotProps={{
              field: {
                clearable: true,
                onClear: () => pushRange(start, null),
              } as any,
              textField: { fullWidth: true, size: "small" },
            }}
            sx={{ width: "100%" }}
          />
        </Box>
      </LocalizationProvider>
    </FilterRowItem>
  );
}
